import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import { dateDifference } from 'lib/datetime/dateUtils'
import type { CruisesContract } from '@luxuryescapes/contract-svc-cruise'
import type { CruiseAPI } from 'api/cruises'

type OfferInclusion = CruiseAPI.OfferInclusion
type DatedOfferInclusion = CruiseAPI.DatedOfferInclusion

type CruiseInclusionsMapParams = {
  inclusions: Array<App.Cruises.OfferInclusion>
  departureId: string
  lowestRateCode: string
  regionCode: string
  /* Displays inclusions for all rate codes, not restricted to the lowest rate */
  allRateCodes?: Array<string>
  /* If true, we want to consider all offer inclusions for the main departure (displayed in the offer tile) */
  isMainDeparture?: boolean
}

type CruiseInclusionsMapResult = {
  luxPlusInclusionsByTier: App.LuxPlusInclusionsByTier | undefined
  standardInclusions: Array<App.PackageInclusion>
  inclusionDetails: App.Cruises.OfferInclusionDetails
}

export function cruiseInclusionsMap({
  inclusions,
  departureId,
  lowestRateCode,
  allRateCodes,
  regionCode,
  isMainDeparture = false,
}: CruiseInclusionsMapParams): CruiseInclusionsMapResult {
  const rateCodeFilters = allRateCodes?.length ? allRateCodes : [lowestRateCode]
  const datedInclusions = inclusions.filter(isDatedOfferInclusion)

  const filteredInclusions = isMainDeparture ? datedInclusions : datedInclusions
    .filter(({ departureIds, rateCodes }) => (
      (!departureIds.length || departureIds.includes(departureId)) &&
      (!rateCodes.length || rateCodeFilters.some(rateFilter => rateCodes.includes(rateFilter)))
    ))

  return {
    luxPlusInclusionsByTier: luxPlusInclusionsByTierMap(filteredInclusions, regionCode),
    standardInclusions: standardInclusionsMap(filteredInclusions, regionCode),
    inclusionDetails: cruiseInclusionDetailsMap(filteredInclusions),
  }
}

export function cruiseEvergreenInclusionsMap(
  inclusions: Array<OfferInclusion>,
): Array<App.PackageInclusion> {
  return inclusions
    .filter((inclusion) => inclusion.type === 'EVERGREEN')
    .flatMap((inclusion) => inclusion.items.map((item) => ({
      id: item.id,
      description: item.description ?? '',
      type: inclusion.type,
      parentCategory: item.type as App.PackageInclusionParentCategory,
      ...getInclusionItemCategories(item),
      isHighlighted: false,
      symbol: item.icon as App.OfferFeatureSymbol | undefined,
      inclusionGroupId: inclusion.id,
    })))
}

export function cruiseBookingInclusionsMap(
  inclusions: Array<CruisesContract.BookingInclusion>,
  regionCode: string,
): {
  standardInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
} {
  const mappedInclusions = inclusions.map((inclusion) => {
    const description = getInclusionDescription(regionCode, inclusion.description, inclusion.amount, inclusion.currency)

    return {
      id: inclusion.id,
      description,
      type: inclusion.inclusionType,
      parentCategory: inclusion.inclusionItemType as App.PackageInclusionParentCategory,
      ...getInclusionItemCategories(inclusion),
      isHighlighted: inclusion.inclusionType === 'LUX_PLUS',
      symbol: getInclusionSymbol(inclusion.inclusionItemType),
    }
  })

  const standardInclusions = dedupePackageInclusionsByDescription(
    mappedInclusions.filter((inclusion) => inclusion.type === 'STANDARD'),
  )
  const luxPlusInclusions = mappedInclusions.filter((inclusion) => inclusion.type === 'LUX_PLUS')

  return {
    standardInclusions,
    luxPlusInclusions,
  }
}

function luxPlusInclusionsByTierMap(
  inclusions: Array<DatedOfferInclusion>,
  regionCode: string,
): App.LuxPlusInclusionsByTier | undefined {
  const luxPlusInclusionGroup = getFirstLuxPlusInclusion(inclusions)

  if (!luxPlusInclusionGroup?.items?.length) return

  const packageInclusions: Array<App.PackageInclusion> = luxPlusInclusionGroup.items.map((item) => {
    const description = getInclusionDescription(regionCode, item.description, item.creditAmount, item.creditCurrency)

    return {
      id: item.id,
      description,
      type: luxPlusInclusionGroup.type,
      parentCategory: item.type,
      ...getInclusionItemCategories(item),
      isHighlighted: luxPlusInclusionGroup.type === 'LUX_PLUS',
      symbol: getInclusionSymbol(item.type, item.icon),
      inclusionGroupId: luxPlusInclusionGroup.id,
    }
  })

  if (!packageInclusions.length) return

  return {
    // Cruise only has one tier of luxplus inclusions for now
    base: packageInclusions,
  }
}

function standardInclusionsMap(
  inclusions: Array<DatedOfferInclusion>,
  regionCode: string,
): Array<App.PackageInclusion> {
  const standardInclusionGroups = getStandardInclusions(inclusions)
  if (!standardInclusionGroups.length) return []

  const packageInclusions = standardInclusionGroups.flatMap((group) => (
    group.items.map((item) => {
      const description = getInclusionDescription(regionCode, item.description, item.creditAmount, item.creditCurrency)

      return {
        id: item.id,
        description,
        isHighlighted: item.isHighlighted,
        type: group.type,
        parentCategory: item.type,
        ...getInclusionItemCategories(item),
        symbol: getInclusionSymbol(item.type, item.icon),
        inclusionGroupId: group.id,
      }
    })
  ))

  return dedupePackageInclusionsByDescription(packageInclusions)
}

function cruiseInclusionDetailsMap(inclusions: Array<DatedOfferInclusion>): App.Cruises.OfferInclusionDetails {
  const standardInclusions = getStandardInclusions(inclusions)
  const luxPlusInclusion = getFirstLuxPlusInclusion(inclusions)

  const hasNewStandardInclusion = standardInclusions.some((inclusion) => (
    dateDifference(new Date(), new Date(inclusion.startDate)).days < 7
  ))
  const hasNewLuxPlusInclusion = !!luxPlusInclusion
    && dateDifference(new Date(), new Date(luxPlusInclusion.startDate)).days < 7

  const endDateCandidates = [
    ...standardInclusions.map((inclusion) => inclusion.endDate),
    luxPlusInclusion?.endDate,
  ].filter((endDate): endDate is string => !!endDate)

  return {
    hasNewInclusion: hasNewStandardInclusion || hasNewLuxPlusInclusion,
    inclusionEndDate: getEarliestEndDate(endDateCandidates) ?? '',
  }
}

function getEarliestEndDate(endDates: Array<string>): string | undefined {
  if (!endDates.length) return

  return endDates.reduce((earliest, endDate) => (
    new Date(endDate) < new Date(earliest) ? endDate : earliest
  ))
}

function getStandardInclusions(
  inclusions: Array<DatedOfferInclusion>,
): Array<DatedOfferInclusion> {
  return inclusions
    .filter(({ type }) => type === 'STANDARD')
    .sort(compareDatedInclusionsByStartDate)
}

/**
 * There should not be more than one group of LuxPlus inclusions, but if there is, we will use only the first one.
 */
function getFirstLuxPlusInclusion(
  inclusions: Array<DatedOfferInclusion>,
): DatedOfferInclusion | undefined {
  return inclusions.find(({ type }) => type === 'LUX_PLUS')
}

function compareDatedInclusionsByStartDate(
  left: DatedOfferInclusion,
  right: DatedOfferInclusion,
): number {
  const startDateComparison = new Date(left.startDate).getTime() - new Date(right.startDate).getTime()
  if (startDateComparison !== 0) return startDateComparison

  return left.id.localeCompare(right.id)
}

function dedupePackageInclusionsByDescription(
  inclusions: Array<App.PackageInclusion>,
): Array<App.PackageInclusion> {
  const seenDescriptions = new Set<string>()

  return inclusions.filter((inclusion) => {
    const descriptionKey = inclusion.description.toLowerCase()
    if (seenDescriptions.has(descriptionKey)) return false

    seenDescriptions.add(descriptionKey)
    return true
  })
}

function getInclusionItemCategories(item: {
  id: string
  categories?: Array<App.Cruises.InclusionItemCategory>
}): { categories?: Array<App.Cruises.InclusionItemCategory> } {
  if (!item.categories?.length) return {}

  return { categories: item.categories }
}

// EVERGREEN inclusions are not tied to an offer date range,
// so they must not be mapped as dated offer inclusions.
function isDatedOfferInclusion(inclusion: App.Cruises.OfferInclusion): inclusion is DatedOfferInclusion {
  return inclusion.type === 'STANDARD' || inclusion.type === 'LUX_PLUS'
}

function getInclusionDescription(
  regionCode: string,
  description?: string | null,
  amount?: number | null,
  currency?: string | null,
): string {
  if (amount && currency) {
    return `Up to ${formatCurrency(amount, currency, regionCode)} Onboard Credit`
  }

  return description ?? ''
}

function getInclusionSymbol(type: string, icon?: string): App.OfferFeatureSymbol | undefined {
  if (type === 'ONBOARD_CREDIT') {
    return 'dollar_sign_in_circle'
  }

  return icon as App.OfferFeatureSymbol | undefined
}
